// pages/ExploreFeedPage.tsx
// Reels-style vertical video feed
import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCourseStore } from '../store/courseStore';
import type { ExploreVideo } from '../types/course';

// Extract YouTube video ID
function extractVideoId(url: string): string | null {
  if (/^[a-zA-Z0-9_-]{11}$/.test(url)) return url;
  const patterns = [
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
    /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([a-zA-Z0-9_-]{11})/,
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
  ];
  for (const p of patterns) { const m = url.match(p); if (m) return m[1]; }
  return null;
}

function formatDuration(s?: number): string {
  if (!s) return '2 min';
  return `${Math.ceil(s / 60)} min`;
}

export default function ExploreFeedPage() {
  const navigate = useNavigate();
  const fetchCourses = useCourseStore((s) => s.fetchCourses);
  const courses = useCourseStore((s) => s.courses);
  const getAllVideosFlat = useCourseStore((s) => s.getAllVideosFlat);

  const [videos, setVideos] = useState<ExploreVideo[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [liked, setLiked] = useState<Set<string>>(new Set());
  const [saved, setSaved] = useState<Set<string>>(new Set());

  // Touch tracking
  const touchStartY = useRef(0);
  const touchDelta = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (courses.length === 0) {
      fetchCourses().then(() => {
        const flat = useCourseStore.getState().getAllVideosFlat();
        setVideos(flat);
      });
    } else {
      setVideos(getAllVideosFlat());
    }
  }, [courses.length, fetchCourses, getAllVideosFlat]);

  const goNext = useCallback(() => {
    setCurrentIndex(prev => Math.min(prev + 1, videos.length - 1));
  }, [videos.length]);

  const goPrev = useCallback(() => {
    setCurrentIndex(prev => Math.max(prev - 1, 0));
  }, []);

  // Touch handlers for swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
    touchDelta.current = 0;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchDelta.current = touchStartY.current - e.touches[0].clientY;
  };

  const handleTouchEnd = () => {
    if (Math.abs(touchDelta.current) > 60) {
      if (touchDelta.current > 0) goNext();
      else goPrev();
    }
  };

  const current = videos[currentIndex];
  const ytId = current ? extractVideoId(current.url) : null;
  const showSuggestion = currentIndex > 0 && currentIndex % 5 === 0;

  if (videos.length === 0) {
    return (
      <div className="explore-feed flex items-center justify-center">
        <div className="w-8 h-8 border-3 border-lavender-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="explore-feed"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Back Button */}
      <button
        onClick={() => navigate('/')}
        className="absolute top-4 left-4 z-50 w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center safe-top"
      >
        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
        </svg>
      </button>

      {/* Counter */}
      <div className="absolute top-5 right-4 z-50 safe-top">
        <span className="text-xs text-white/60 font-medium bg-black/30 px-2.5 py-1 rounded-full backdrop-blur-sm">
          {currentIndex + 1}/{videos.length}
        </span>
      </div>

      {/* Video */}
      {ytId && (
        <div className="w-full h-full bg-black">
          <iframe
            key={ytId}
            src={`https://www.youtube.com/embed/${ytId}?autoplay=1&controls=0&rel=0&modestbranding=1&playsinline=1&loop=1&playlist=${ytId}&showinfo=0&iv_load_policy=3`}
            className="w-full h-full border-none"
            allow="autoplay; encrypted-media"
            allowFullScreen
          />
        </div>
      )}

      {/* Right Side Actions */}
      <div className="absolute right-4 bottom-32 z-40 flex flex-col items-center gap-5">
        <button
          onClick={() => {
            const next = new Set(liked);
            if (next.has(current.id)) next.delete(current.id); else next.add(current.id);
            setLiked(next);
          }}
          className="flex flex-col items-center gap-1"
        >
          <div className={`w-11 h-11 rounded-full flex items-center justify-center ${liked.has(current?.id) ? 'bg-coral-500' : 'bg-black/40 backdrop-blur-sm'}`}>
            <svg className="w-5 h-5 text-white" fill={liked.has(current?.id) ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
            </svg>
          </div>
          <span className="text-xs text-white/80 font-medium">Like</span>
        </button>

        <button
          onClick={() => {
            const next = new Set(saved);
            if (next.has(current.id)) next.delete(current.id); else next.add(current.id);
            setSaved(next);
          }}
          className="flex flex-col items-center gap-1"
        >
          <div className={`w-11 h-11 rounded-full flex items-center justify-center ${saved.has(current?.id) ? 'bg-lavender-500' : 'bg-black/40 backdrop-blur-sm'}`}>
            <svg className="w-5 h-5 text-white" fill={saved.has(current?.id) ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" />
            </svg>
          </div>
          <span className="text-xs text-white/80 font-medium">Save</span>
        </button>

        <button
          onClick={() => {
            setCurrentIndex(prev => prev); // Force re-render / replay
            if (containerRef.current) {
              const iframe = containerRef.current.querySelector('iframe');
              if (iframe) {
                const src = iframe.src;
                iframe.src = '';
                iframe.src = src;
              }
            }
          }}
          className="flex flex-col items-center gap-1"
        >
          <div className="w-11 h-11 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
            </svg>
          </div>
          <span className="text-xs text-white/80 font-medium">Replay</span>
        </button>
      </div>

      {/* Bottom Info */}
      <div className="absolute bottom-8 left-4 right-20 z-40 safe-bottom">
        <h3 className="text-white font-bold text-lg leading-snug line-clamp-2 drop-shadow-lg">
          {current?.title}
        </h3>
        <p className="text-white/70 text-sm mt-1 drop-shadow-md">
          {current?.subjectName} • {current?.chapterName}
        </p>
        <div className="flex items-center gap-2 mt-2">
          <span className="text-xs bg-white/20 backdrop-blur-sm text-white px-2.5 py-1 rounded-full font-medium">
            {formatDuration(current?.duration)}
          </span>
          {current?.difficulty && (
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium backdrop-blur-sm ${
              current.difficulty === 'Easy' ? 'bg-mint-500/30 text-mint-200' :
              current.difficulty === 'Medium' ? 'bg-amber-500/30 text-amber-200' :
              'bg-coral-500/30 text-coral-200'
            }`}>
              {current.difficulty}
            </span>
          )}
        </div>
      </div>

      {/* Swipe Hint */}
      {currentIndex === 0 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-50 text-white/40 text-xs font-medium animate-bounce safe-bottom">
          ↑ Swipe up for next
        </div>
      )}

      {/* Smart Suggestion Card */}
      {showSuggestion && (
        <div className="absolute inset-x-4 bottom-36 z-50 glass-dark rounded-2xl p-4 safe-bottom animate-scale-in">
          <p className="text-white/90 text-sm font-medium mb-3">
            💡 Want a structured path?
          </p>
          <button
            onClick={() => navigate('/course')}
            className="w-full touch-btn gradient-mint text-white text-sm py-3"
          >
            Try Course →
          </button>
        </div>
      )}
    </div>
  );
}
